import { ReactNode, useLayoutEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useUniqueId } from '~/utils/compute';

const useDocumentTitle = (title: string) => {
  const defaultTitle = useRef<string | null>(null);

  // save default title on mount
  useLayoutEffect(() => {
    defaultTitle.current = document.title;
  }, []);

  // update document title
  useLayoutEffect(() => {
    window.document.title = title;
    // reset document title on unmount
    return () => {
      if (defaultTitle.current) {
        document.title = defaultTitle.current;
      }
    };
  }, [title]);
};

type PageProps = {
  /** The main title of the page, which is required and will be used to set the document title for better SEO and accessibility. */
  title: string;
  /** An optional subtitle that provides additional context about the page content, enhancing user understanding and engagement. */
  subtitle?: string;
  /** Whether to hide the header visually while keeping it accessible to screen readers. */
  hideHeader?: boolean;
  /** The main content of the page. */
  children?: ReactNode;
  /** Additional CSS classes to apply to the page container. */
  className?: string;
};

/**
 * Page component for displaying a structured page layout.
 * Includes a header with title and optional subtitle, and a content area for child components.
 * The header can be visually hidden while remaining accessible to screen readers.
 * The document title is automatically updated based on the page title prop.
 */
export function Root({
  title,
  subtitle,
  children,
  className = '',
  hideHeader = false,
}: PageProps) {
  const titleId = useUniqueId();
  const subtitleId = useUniqueId();

  // update document title
  useDocumentTitle(title);

  return (
    <section
      className={twMerge(
        'relative flex flex-col gap-12 rounded-md bg-surface-container-lowest px-6 py-6',
        className,
      )}
      aria-labelledby={titleId}
      aria-describedby={subtitleId}
      data-component="corso-page"
    >
      <header className={twMerge('flex flex-col', hideHeader && 'sr-only')}>
        <h1 id={titleId} className="heading-md text-on-surface">
          {title}
        </h1>
        {subtitle && (
          <p id={subtitleId} className="subheading-md text-on-surface-variant">
            {subtitle}
          </p>
        )}
      </header>
      {children}
    </section>
  );
}
Root.displayName = 'Page Root';

type PageSectionProps = {
  /**
   * The title of the page section.
   */
  title?: string;
  /**
   * The subtitle of the page section, which provides additional context or description for the content within the section.
   */
  subtitle?: string | null;
  /**
   * Additional CSS classes to apply to the page section, allowing for custom styling and layout adjustments.
   */
  className?: string;
  /**
   * The content of the page section. */
  children?: ReactNode;
};
/**
 * PageSection component for displaying a distinct section within a page layout.
 * Each section can have its own title and subtitle, and is visually separated from other sections.
 * The section header is only rendered if a title or subtitle is provided, allowing for flexible content organization.
 * Accessibility attributes are included to associate the section with its header content when present.
 */
export function Section({
  title,
  subtitle,
  className,
  children,
}: PageSectionProps) {
  const titleId = useUniqueId();
  const subtitleId = useUniqueId();

  return (
    <section
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={subtitle ? subtitleId : undefined}
      className={twMerge('flex flex-col gap-4', className)}
      data-component="corso-page-section"
    >
      {(title || subtitle) && (
        <header className="flex flex-col">
          {title && (
            <h2 id={titleId} className={twMerge('heading-sm text-on-surface')}>
              {title}
            </h2>
          )}
          {subtitle && (
            <p
              id={subtitleId}
              className="subheading-sm text-on-surface-variant"
            >
              {subtitle}
            </p>
          )}
        </header>
      )}
      {children}
    </section>
  );
}
Section.displayName = 'Page Section';

export default {
  Root,
  Section,
};
